import { useState, useEffect } from 'react';

interface OrderDiscount {
  type: 'percentage' | 'fixed';
  value: number;
}

interface OrderDiscountModalProps {
  onClose: () => void;
  onApply: (discount: OrderDiscount) => void;
  currentDiscount: OrderDiscount | null;
}

export default function OrderDiscountModal({ onClose, onApply, currentDiscount }: OrderDiscountModalProps) {
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>(
    currentDiscount?.type || 'percentage'
  );
  const [discountValue, setDiscountValue] = useState<string>(
    currentDiscount?.value.toString() || ''
  );
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleApply = () => {
    const value = parseFloat(discountValue);
    
    if (isNaN(value) || value <= 0) {
      setError('Please enter a valid discount amount');
      return;
    }

    if (discountType === 'percentage' && value > 100) {
      setError('Percentage cannot exceed 100%');
      return;
    }

    onApply({
      type: discountType,
      value: value
    });
    onClose();
  };

  const handleQuickAmount = (amount: number) => {
    if (discountType === 'percentage') {
      setDiscountValue(amount.toString());
    } else {
      setDiscountValue(amount.toString());
    }
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 whitespace-nowrap">Order Discount</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Discount Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">
              Discount Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  setDiscountType('percentage');
                  setDiscountValue('');
                  setError('');
                }}
                className={`px-4 py-3 rounded-lg font-semibold text-sm whitespace-nowrap transition-all cursor-pointer ${
                  discountType === 'percentage'
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-percent-line mr-2"></i>
                Percentage
              </button>
              <button
                onClick={() => {
                  setDiscountType('fixed');
                  setDiscountValue('');
                  setError('');
                }}
                className={`px-4 py-3 rounded-lg font-semibold text-sm whitespace-nowrap transition-all cursor-pointer ${
                  discountType === 'fixed'
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className="ri-money-dollar-circle-line mr-2"></i>
                Fixed Amount
              </button>
            </div>
          </div>

          {/* Quick Amount Buttons */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3 whitespace-nowrap">
              Quick Select
            </label>
            <div className="grid grid-cols-4 gap-2">
              {discountType === 'percentage' ? (
                <>
                  <button
                    onClick={() => handleQuickAmount(5)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    5%
                  </button>
                  <button
                    onClick={() => handleQuickAmount(10)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    10%
                  </button>
                  <button
                    onClick={() => handleQuickAmount(15)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    15%
                  </button>
                  <button
                    onClick={() => handleQuickAmount(20)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    20%
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleQuickAmount(5)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    $5
                  </button>
                  <button
                    onClick={() => handleQuickAmount(10)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    $10
                  </button>
                  <button
                    onClick={() => handleQuickAmount(15)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    $15
                  </button>
                  <button
                    onClick={() => handleQuickAmount(20)}
                    className="px-3 py-2.5 bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-600 rounded-lg font-semibold text-sm whitespace-nowrap transition-colors cursor-pointer"
                  >
                    $20
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Manual Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2 whitespace-nowrap">
              {discountType === 'percentage' ? 'Discount Percentage' : 'Discount Amount'}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <span className="text-gray-500 font-semibold">
                  {discountType === 'percentage' ? '%' : '$'}
                </span>
              </div>
              <input
                type="number"
                value={discountValue}
                onChange={(e) => {
                  setDiscountValue(e.target.value);
                  setError('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleApply();
                  }
                }}
                placeholder={discountType === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:border-orange-500 focus:outline-none text-base"
                step={discountType === 'percentage' ? '1' : '0.01'}
                min="0"
                max={discountType === 'percentage' ? '100' : undefined}
              />
            </div>
            {error && (
              <p className="mt-2 text-sm text-red-600 whitespace-nowrap">
                <i className="ri-error-warning-line mr-1"></i>
                {error}
              </p>
            )}
          </div>

          {/* Preview */}
          {discountValue && !error && parseFloat(discountValue) > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                  Discount Preview:
                </span>
                <span className="text-lg font-bold text-orange-600 whitespace-nowrap">
                  {discountType === 'percentage' 
                    ? `${parseFloat(discountValue)}%` 
                    : `$${parseFloat(discountValue).toFixed(2)}`}
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-check-line mr-2"></i>
            Apply Discount
          </button>
        </div>
      </div>
    </div>
  );
}
